import { parseDiagnostic } from "../diagnostics";

type DiagnosticEnvironment = { DEV?: boolean; VITE_DEPLOYMENT_ENV?: string };
const environment = (import.meta as ImportMeta & { env?: DiagnosticEnvironment }).env;
let hosted: { url: string; token: () => string | undefined } | undefined;
/** Called once by the app with its existing user session, never a service credential. */
export function configureCallDiagnostics(url: string, token: () => string | undefined) {
  if (environment?.VITE_DEPLOYMENT_ENV !== "dev") return;
  try {
    hosted = { url: new URL("/diagnostics/calls", url).href, token };
  } catch {
    hosted = undefined; // Diagnostics configuration cannot prevent app startup.
  }
}

/** Technical metadata only; never send credentials, names or raw SDK attributes. */
export function createCallDiagnostics(call: string, participant: string) {
  const enabled = environment?.DEV === true || environment?.VITE_DEPLOYMENT_ENV === "dev";
  const attempt = enabled ? crypto.randomUUID() : "";
  const started = performance.now();
  let pending = 0;
  let lastMetrics = -Infinity;
  const emit = (event: string, metrics: Record<string, number> = {}) => {
    if (!enabled || pending >= 32) return;
    const target = hosted;
    const token = target?.token();
    if (!environment?.DEV && (!target || !token)) return;
    const raw = { call, participant, attempt, event, elapsedMs: Math.round(performance.now() - started), metrics };
    const record = parseDiagnostic(raw);
    if (!record) return;
    pending++;
    void fetch(target?.url ?? "/__call-diagnostics", {
      method: "POST",
      headers: { "content-type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(record),
      credentials: "omit",
      referrerPolicy: "no-referrer",
      signal: AbortSignal.timeout(1000),
    })
      .catch(() => {})
      .finally(() => {
        pending--;
      });
  };
  return {
    emit,
    metrics(read: () => Record<string, number>) {
      if (!enabled || performance.now() - lastMetrics < 5000) return;
      lastMetrics = performance.now();
      try {
        emit("quality", read());
      } catch {
        /* Diagnostics cannot break a call. */
      }
    },
  };
}
